/*
  Is this page running inside the ClassTutor phone app (../../native), and if so which phone?
  Ported from Tazkiyah (tazkiyah/src/native/nativeApp.ts). The phone app opens this same web app
  and adds window.Capacitor with its plugins; on the website every helper quietly says "not the
  phone app" so callers fall back to web behaviour. No Capacitor npm package is needed here.
*/
export type NativePlatform = "ios" | "android";

type PluginMethods = Record<string, (options?: unknown) => Promise<unknown>>;
interface CapacitorBridge {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
  Plugins?: Record<string, PluginMethods | undefined>;
}

const bridge = (): CapacitorBridge | undefined => (globalThis as { Capacitor?: CapacitorBridge }).Capacitor;

export const isNativeApp = (): boolean => Boolean(bridge()?.isNativePlatform?.());

export function nativePlatform(): NativePlatform | null {
  if (!isNativeApp()) return null;
  const platform = bridge()?.getPlatform?.();
  return platform === "ios" || platform === "android" ? platform : null;
}

export function nativePlugin<T>(name: string): T | null {
  const cap = bridge();
  if (!isNativeApp() || !cap?.isPluginAvailable?.(name)) return null;
  return (cap.Plugins?.[name] as T | undefined) ?? null;
}

export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
export const isApple = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isAndroidBrowser = () => /android/i.test(navigator.userAgent) && !isNativeApp();

export function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/ipad/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "iPad";
  if (/iphone|ipod/i.test(ua)) return "iPhone";
  if (/android/i.test(ua)) return "Android";
  return "Computer";
}

/** Open a Zoom link or recording: the system browser / Zoom app on a phone, a new tab on the web. */
export async function openExternal(url: string) {
  interface BrowserPlugin { open(o: { url: string }): Promise<void> }
  const browser = nativePlugin<BrowserPlugin>("Browser");
  if (browser && !/^zoom(us|mtg):/.test(url)) { await browser.open({ url }); return; }
  window.open(url, "_blank", "noopener");
}

/** Where to get the phone app. Null until published, so the screen says "coming soon". */
export const STORE_LINKS: { ios: string | null; android: string | null } = { ios: null, android: null };
